import {
  FormControl,
  styled,
  Typography,
  Switch as MuiSwitch,
} from '@mui/material';
import { ChangeEventHandler, forwardRef } from 'react';
import { RefCallBack } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type SwitchProps = {
  ariaLabel: string;
  textChecked: string;
  textDefault: string;
  checked?: boolean;
  disabled?: boolean;
  label?: string;
  hasIcon?: boolean;
  width?: number;
  onChange?: ChangeEventHandler<HTMLInputElement>;
};

export const Switch = forwardRef<RefCallBack, SwitchProps>(
  (
    {
      ariaLabel,
      disabled,
      checked,
      hasIcon,
      label,
      textChecked,
      textDefault,
      width = 100,
      onChange,
    },
    ref
  ) => {
    const { t } = useTranslation();

    return (
      <StyledFormControl disabled={disabled}>
        {label && (
          <StyledTypography
            className={disabled ? 'disabled' : ''}
            variant="caption"
          >
            {`${t(label)}:`}
          </StyledTypography>
        )}

        <StyledSwitch className={hasIcon ? 'has-icon' : ''} style={{ width }}>
          <MuiSwitch
            sx={{ width }}
            checked={checked}
            inputProps={{ 'aria-label': ariaLabel }}
            onChange={onChange}
            inputRef={ref}
          />

          <StyledLabel className={checked ? 'textChecked' : 'textDefault'}>
            <Typography variant="subtitle1">
              {t(checked ? textChecked : textDefault)}
            </Typography>
          </StyledLabel>
        </StyledSwitch>
      </StyledFormControl>
    );
  }
);

Switch.displayName = 'Switch';

const StyledFormControl = styled(FormControl)({
  flexShrink: 0,
});

const StyledTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),

  '&.disabled': {
    color: theme.palette.text.secondary,
  },
}));

const StyledSwitch = styled('div')(({ theme }) => ({
  position: 'relative',

  '&.has-icon': {
    '& .Mui-checked': {
      '& .MuiSwitch-thumb': {
        '&::before': {
          backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 41 41"><path fill="${encodeURIComponent(
            theme.palette.error?.lighter as string
          )}" d="M26.8635 23.105L24.9844 21.2259C25.7246 19.2044 25.3403 17.5245 23.8312 16.1863C22.3222 14.8481 20.6851 14.5064 18.9198 15.1613L17.0406 13.2821C17.5246 12.9689 18.0656 12.7412 18.6635 12.5988C19.2615 12.4564 19.8736 12.3853 20.5 12.3853C22.5215 12.3853 24.237 13.0899 25.6463 14.4993C27.0557 15.9087 27.7604 17.6241 27.7604 19.6457C27.7604 20.2721 27.6821 20.8913 27.5255 21.5035C27.3689 22.1156 27.1483 22.6495 26.8635 23.105ZM32.3729 28.6144L30.6646 26.9061C32.0597 25.8811 33.2769 24.7351 34.3161 23.4681C35.3554 22.2011 36.117 20.9269 36.601 19.6457C35.1774 16.4853 33.042 13.9868 30.1948 12.1504C27.3476 10.3139 24.2583 9.39567 20.9271 9.39567C19.7312 9.39567 18.5069 9.50956 17.2542 9.73734C16.0014 9.96511 15.0191 10.2356 14.3073 10.5488L12.3427 8.5415C13.3392 8.08595 14.6134 7.68734 16.1651 7.34567C17.7168 7.004 19.233 6.83317 20.7135 6.83317C24.7851 6.83317 28.5078 7.99341 31.8818 10.3139C35.2557 12.6344 37.7257 15.745 39.2917 19.6457C38.5514 21.4679 37.5976 23.1335 36.4302 24.6425C35.2628 26.1516 33.9104 27.4755 32.3729 28.6144ZM34.85 38.2665L27.675 31.2196C26.6785 31.6182 25.5538 31.9243 24.301 32.1379C23.0483 32.3514 21.7812 32.4582 20.5 32.4582C16.3431 32.4582 12.5705 31.2979 9.18229 28.9774C5.79409 26.657 3.30277 23.5464 1.70833 19.6457C2.27777 18.1651 3.06788 16.7201 4.07864 15.3108C5.0894 13.9014 6.32083 12.5561 7.77291 11.2748L2.39166 5.89359L4.18541 4.05713L36.5156 36.3873L34.85 38.2665ZM9.52395 13.0686C8.47048 13.8373 7.4526 14.8481 6.47031 16.1009C5.48802 17.3537 4.78333 18.5353 4.35624 19.6457C5.80833 22.8061 7.99357 25.3045 10.912 27.141C13.8304 28.9774 17.1403 29.8957 20.8417 29.8957C21.7812 29.8957 22.7066 29.8387 23.6177 29.7248C24.5288 29.6109 25.2121 29.4401 25.6677 29.2123L22.9344 26.479C22.6212 26.6214 22.2368 26.7281 21.7812 26.7993C21.3257 26.8705 20.8986 26.9061 20.5 26.9061C18.5069 26.9061 16.7986 26.2085 15.375 24.8134C13.9514 23.4182 13.2396 21.6957 13.2396 19.6457C13.2396 19.2186 13.2752 18.7915 13.3463 18.3644C13.4175 17.9373 13.5243 17.553 13.6667 17.2113L9.52395 13.0686Z" /></svg>')`,
        },
      },
    },

    '& .MuiSwitch-thumb': {
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100%',
        height: '100%',
        left: 0,
        top: 0,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 41 41"><path fill="${encodeURIComponent(
          theme.palette.success?.lighter as string
        )}" d="M20.5 26.9064C22.5215 26.9064 24.237 26.2017 25.6463 24.7924C27.0557 23.383 27.7604 21.6675 27.7604 19.646C27.7604 17.6245 27.0557 15.909 25.6463 14.4996C24.237 13.0903 22.5215 12.3856 20.5 12.3856C18.4785 12.3856 16.763 13.0903 15.3536 14.4996C13.9443 15.909 13.2396 17.6245 13.2396 19.646C13.2396 21.6675 13.9443 23.383 15.3536 24.7924C16.763 26.2017 18.4785 26.9064 20.5 26.9064ZM20.5 24.4293C19.1618 24.4293 18.03 23.9667 17.1047 23.0413C16.1793 22.116 15.7167 20.9842 15.7167 19.646C15.7167 18.3078 16.1793 17.176 17.1047 16.2507C18.03 15.3253 19.1618 14.8627 20.5 14.8627C21.8382 14.8627 22.97 15.3253 23.8953 16.2507C24.8207 17.176 25.2833 18.3078 25.2833 19.646C25.2833 20.9842 24.8207 22.116 23.8953 23.0413C22.97 23.9667 21.8382 24.4293 20.5 24.4293ZM20.5 32.4585C16.3431 32.4585 12.5847 31.2769 9.22499 28.9137C5.86527 26.5505 3.35972 23.4613 1.70833 19.646C3.35972 15.8307 5.86527 12.7415 9.22499 10.3783C12.5847 8.01509 16.3431 6.8335 20.5 6.8335C24.6569 6.8335 28.4153 8.01509 31.775 10.3783C35.1347 12.7415 37.6403 15.8307 39.2917 19.646C37.6403 23.4613 35.1347 26.5505 31.775 28.9137C28.4153 31.2769 24.6569 32.4585 20.5 32.4585ZM20.5 29.896C23.9451 29.896 27.1127 28.9635 30.0026 27.0986C32.8925 25.2337 35.092 22.7495 36.601 19.646C35.092 16.5425 32.8925 14.0583 30.0026 12.1934C27.1127 10.3285 23.9451 9.396 20.5 9.396C17.0549 9.396 13.8873 10.3285 10.9974 12.1934C8.10746 14.0583 5.89374 16.5425 4.35624 19.646C5.89374 22.7495 8.10746 25.2337 10.9974 27.0986C13.8873 28.9635 17.0549 29.896 20.5 29.896Z"/></svg>')`,
      },
    },
  },
}));

const StyledLabel = styled('div')(({ theme }) => ({
  color: theme.palette.text.secondary,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  top: 0,
  pointerEvents: 'none',

  '&.textChecked': { left: theme.spacing(1) },
  '&.textDefault': { right: theme.spacing(1) },
}));
